import type { TemplateRef } from '@angular/core';
import type { IconColor, IconSetAlias } from '../../directives/icon/icon';
import type { ConfirmationOptions } from '../dialog/models';

export interface UploadOptions {
    multiple: boolean;
    withDragAndDrop: boolean;
    allowFolderSelection: boolean;
    acceptedFileTypes: string | string[];
}

export interface LabelOptions {
    file: string;
    folder: string;
    dnd: string;
}

export interface IconOption {
    icon: string;
    type: 'outlined' | 'filled';
    set: IconSetAlias;
    context?: IconColor;
    size: 'lg' | 'md' | 'sm' | 'xs';
}

export interface IconOptions {
    file: string | Partial<IconOption>;
    folder: string | Partial<IconOption>;
}

export interface AriaOptions {
    idPrefix: string;
    ariaLabelSuffix: string;
}

export const DEFAULT_ARIA_OPTIONS: AriaOptions = {
    idPrefix: 'file-upload',
    ariaLabelSuffix: 'file upload',
};

export interface BaseFileConfig {
    maxFileCount?: number;
    sizeValidation?: 'per-file' | 'total' | 'both';
    maxSizePerFileMB?: number;
    maxTotalSizeMB?: number;
    errorHandler?: 'default' | 'custom';
    nonBlockingTypeInvalid?: boolean;
    nonBlockingPerFileOversize?: boolean;
}

export type FileTypePolicy =
    | {
          kind: 'configured-allowlist';
          additionalAllowedFileExtensions?: readonly string[];
      }
    | {
          kind: 'document-denylist';
          allowZipArchives?: boolean;
      };

export interface FileInputConfig extends BaseFileConfig {
    maxFileCount?: number;
    fileTypePolicy?: FileTypePolicy;
}

export interface ProcessingMetadata {
    originalFileCount: number;
    validFileCount: number;
    totalSizeBytes: number;
    rejectedFiles?: Array<{
        name: string;
        size: number;
    }>;
}

export interface FileInputResult {
    files: File[];
    clearInput: () => void;
    metadata: ProcessingMetadata;
}

export interface FileUploadIssue {
    title: string;
    message: string;
    rejectedFiles: Array<{
        name: string;
        size: number;
    }>;
}

export interface FileValidationResult {
    acceptedFiles: File[];
    issues: FileUploadIssue[];
    hasBlockingIssue: boolean;
    metadata: ProcessingMetadata;
}

export interface DuplicateCheckResult {
    uniqueFiles: File[];
    duplicateNames: string[];
}

export interface DuplicateDialogContext {
    clientDuplicates: string[];
}

export type DuplicateDialogOptions = Partial<
    Omit<
        ConfirmationOptions,
        'contentTpl' | 'contentTplContext' | 'contentHtml'
    >
>;

export interface DuplicateDialogConfig {
    duplicateNames: string[];
    dialogOptions?: DuplicateDialogOptions;
    dialogTemplate?: TemplateRef<DuplicateDialogContext>;
    dialogTemplateContext?: Record<string, unknown>;
}

export interface FilePickerTemplateContext {
    $implicit?: undefined;
    disabled: boolean;
    browse: () => void;
    id: string;
}
